import { LitElement, html, css } from 'lit';
import { property, query, customElement } from 'lit/decorators.js';

import {
  authenticatedAPICall,
  isSuccessfulAPIResponse,
} from '../../client/api-client';
import { Account } from '../../types/types';
import { sharedStyles } from '../shared-styles';

@customElement('search-dropdown-account')
export class SearchDropdownAccount extends LitElement {
  static styles = [
    sharedStyles,
    css`
      :host {
        width: 100%;
      }

      .wrapper {
        position: relative;
      }

      .inner-display {
        width: calc(100% - 10px);
      }

      .row {
        display: flex;
        flex-direction: row;
        width: 100%;
      }

      .column {
        width: 100%;
        padding: 0;
      }

      .selectables {
        position: absolute;
        z-index: 99;
        color: var(--dark-background-color);
        background-color: white;
        display: none;
        width: 100%;
        border: 1px solid var(--dark-background-color);
        border-radius: 4px;
        max-height: 300px;
        overflow-y: auto;
      }

      :host([selectablesVisible]) .selectables {
        display: block;
      }

      input {
        width: calc(100% - 4px);
        height: 22px;
        padding: 5px;
        border-radius: 5px;
        font-size: 16px;
      }

      .search-option {
        border-bottom: 1px solid var(--dark-background-color);
      }

      .search-option:hover {
        background-color: var(--background-color-hover);
      }
    `,
  ];

  @property({ type: Array, attribute: true })
  public selectable: Account[] = [];

  @property({ type: Object, attribute: true, reflect: true })
  public selected?: Account;

  @property({ type: Boolean, reflect: true })
  public selectablesVisible = false;

  @query('#searchInputAccount')
  public searchInput?: HTMLInputElement;

  constructor() {
    super();
    document.addEventListener('click', this._handleClick);
  }

  protected firstUpdated(): void {
    this.listAccounts();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this._handleClick);
  }

  _handleClick = (e: Event) => {
    // If not cur element is clicked
    if (!e.composedPath().includes(this)) {
      this.selectablesVisible = false;
      if (this.searchInput) {
        this.searchInput.value = this.getInputValue();
      }
    }
  };

  render() {
    return html`
      <div class="wrapper">
        <div class="row selected-display">
          <div class="inner-display">
            <input
              type="text"
              id="searchInputAccount"
              name="searchInputAccount"
              value="${this.getInputValue()}"
              @click="${() => {
                this.selectablesVisible = !this.selectablesVisible;
                if (this.searchInput && this.selectablesVisible) {
                  this.searchInput.value = '';
                }
              }}"
              @keydown=${() => {
                this.selectablesVisible = true;
                this.listWithBackoff();
              }}
            />
          </div>
        </div>
        <div class="selectables">
          <div class="column">${this.renderSelectables()}</div>
        </div>
      </div>
    `;
  }

  renderSelectables = () => {
    if (this.selectable.length === 0) {
      return html`
        <div class="search-option">No accounts found for current search.</div>
      `;
    }
    return html`
      ${this.selectable.map(item => {
        return html`
          <div
            class="search-option"
            @click=${() => {
              this.selectablesVisible = false;
              this.selected = item;
              if (this.searchInput) {
                this.searchInput.value = item.name;
              }
              let selectedAccount = {};
              if (this.selected) {
                selectedAccount = this.selected;
              }
              const event = new CustomEvent('change', {
                detail: {
                  value: JSON.stringify(selectedAccount),
                },
              });
              this.dispatchEvent(event);
            }}
          >
            ${item.name}
          </div>
        `;
      })}
    `;
  };

  getInputValue = () => {
    if (this.selected) {
      return this.selected.name;
    }
    return 'no account selected';
  };

  listWithBackoff = () => {
    setTimeout(() => {
      this.listAccounts();
    }, 10);
  };

  listAccounts = async () => {
    const filterParts = [];
    if (
      this.searchInput &&
      this.searchInput.value &&
      this.searchInput.value !== 'no account selected'
    ) {
      filterParts.push(`name=${this.searchInput.value}`);
      filterParts.push(`code=${this.searchInput.value}`);
    }

    const result = await authenticatedAPICall(
      `/accounts?${filterParts.join('&')}`,
      'GET',
      new Headers(),
    );
    if (isSuccessfulAPIResponse(result)) {
      this.selectable = result.body;
    }
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'search-dropdown-account': SearchDropdownAccount;
  }
}
